"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var prediction_model_results_viz_1 = require("@tableau/prediction-model-results-viz");
var widgets_1 = require("@tableau/widgets");
var widgets_datagrid_1 = require("@tableau/widgets-datagrid");
var React = require("react");
var Localize_1 = require("../../../../gen-src/Localize");
var RootReducer_1 = require("../../../app/RootReducer");
var download_14px_small_1 = require("../../../img/download_14px_small");
var styles = require("../../../styles/ModelResultStyles");
var NumberUtils_1 = require("../../../util/NumberUtils");
var SortableDataGrid_1 = require("../../base/SortableDataGrid");
var MODEL_COEFFICIENTS_NUM_VAR_WIDTH = 207;
var MODEL_COEFFICIENTS_INTERACTION_WIDTH = 411;
var MODEL_COEFFICIENTS_INTERCEPT_WIDTH = 170;
var ModelResultCoefficients = function () {
    var _a = (0, RootReducer_1.useAppSelector)(function (state) { return state.modelResult; }), modelCoefficients = _a.modelCoefficients, modelFields = _a.modelFields;
    var sortableDataGridComponent = React.useRef(null);
    if (!modelCoefficients || !modelFields) {
        return null;
    }
    var labels = (modelCoefficients === null || modelCoefficients === void 0 ? void 0 : modelCoefficients.labels) || [];
    var values = (modelCoefficients === null || modelCoefficients === void 0 ? void 0 : modelCoefficients.values) || [];
    var rows = [];
    // modelFields is just used to find which ones are ignored.
    // coefficients contains 2 arrays that we need to merge into one array of rows.
    // Also need to find the max value, and the intercept which is the first value
    var max = Math.abs(Math.max.apply(Math, values.slice(1)));
    var min = Math.abs(Math.min.apply(Math, values.slice(1)));
    max = Math.max(max, min);
    var ignoreFields = modelFields.filter(function (n) { return !!n.ignored; });
    var interceptValue = 0;
    labels.forEach(function (element, index) {
        if (index !== 0) {
            var initialLabel = labels[index];
            // Borrowed from Einstein
            var formattedValue = values[index].toFixed(4).replace(/\.?0*$/, '');
            rows.push({
                key: index.toString(),
                data: {
                    label: initialLabel,
                    value: values[index],
                    formattedValue: formattedValue,
                    scaledValue: Math.abs(values[index] / max).toFixed(4),
                },
            });
        }
        else {
            interceptValue = values[index];
        }
    });
    var headerComponent = function (props) { return (React.createElement("div", { className: styles.DataGridHeader },
        React.createElement("div", { className: "".concat(styles.DataGridHeaderText, " ").concat(styles.DataGridHeaderTextSortable) }, props.col.headerText))); };
    // Cell formatter for the scaled importance column
    var ScaledImportanceFormatter = function (props) {
        var colorType = props.row.data.formattedValue >= 0 ? styles.metricsProgressBar : styles.metricsProgressBarErr;
        // Create a proportional bar using background color
        var ParentStyle = {
            height: props.height,
            position: 'relative',
        };
        var CellStyle = {
            position: 'absolute',
            top: '5px',
            left: '0px',
            backgroundColor: colorType,
            borderRadius: '2px',
            width: (Math.abs(props.row.data.value / max) * 100).toString() + '%',
            height: props.height - 16,
        };
        return (React.createElement("div", { style: ParentStyle },
            React.createElement("div", { style: CellStyle })));
    };
    var downloadCSV = function () {
        var csvString = 'Value,Coefficient \n';
        for (var i = 0; i < rows.length; i++) {
            csvString += "\"".concat(rows[i].data.label, "\",").concat(rows[i].data.value, " \n");
        }
        if (!/^data:text\/csv/i.exec(csvString)) {
            csvString = "data:text/csv;charset=utf-8,".concat(csvString);
        }
        var csvFile = encodeURI(csvString);
        var downloadLink = document.createElement('a');
        downloadLink.setAttribute('href', csvFile);
        downloadLink.setAttribute('download', 'Coefficients.csv');
        downloadLink.click();
    };
    var cols = [
        {
            name: 'value',
            dataKey: 'label',
            headerText: Localize_1.Messages.modelResultsCoefficientsValue(),
            sortable: true,
            growToFit: true,
            headerComponent: headerComponent,
        },
        {
            name: 'coefficient',
            dataKey: 'formattedValue',
            headerText: Localize_1.Messages.modelResultsCoefficient(),
            sortable: true,
            growToFit: false,
            preferredWidth: 100,
            headerComponent: headerComponent,
        },
        {
            name: 'scaledImportanceText',
            dataKey: 'scaledValue',
            headerText: '',
            sortable: false,
            growToFit: false,
            preferredWidth: 68,
            headerComponent: headerComponent,
        },
        {
            name: 'scaledImportance',
            dataKey: 'scaledValue',
            headerText: Localize_1.Messages.modelResultsCoefficientsScaledImportance(),
            sortable: true,
            growToFit: true,
            headerComponent: headerComponent,
            cellComponent: ScaledImportanceFormatter,
        },
    ];
    // Row sorter which is passed to child so it knows how to sort
    var dataGridRowSorter = function (rows, sortKey, sortAscending) {
        if (sortKey === 'label') {
            rows.sort(function (a, b) {
                var aStr = a.data.label;
                var bStr = b.data.label;
                return sortAscending ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
            });
        }
        else {
            rows.sort(function (a, b) {
                return sortAscending ? Math.abs(b.data.value) - Math.abs(a.data.value) : Math.abs(a.data.value) - Math.abs(b.data.value);
            });
        }
        return rows;
    };
    // Apply a default sort
    dataGridRowSorter(rows, 'coefficient', true);
    var sortedDataGridProps = {
        dataGridProps: {
            cols: cols,
            rowStore: new widgets_datagrid_1.ArrayRowStore(rows),
            showHeaders: true,
            showHorizontalGridLines: true,
            showRowBanding: false,
            sortKey: 'scaledImportance',
            sortAscending: true,
            emptyChild: React.createElement("div", { className: styles.ModelCoefficientsEmptyChildStyle }, Localize_1.Messages.modelResultsCoefficientsNoResults()),
        },
        dataGridRowSorter: dataGridRowSorter,
        dataGridSelection: null,
    };
    // From experimentation I don't see a need to debounce as of yet.
    // sortableDataGridComponent is a ref to the child datagrid so we can hand it a new set of rows, rather than pass using props
    var filterChanged = function (e) {
        var _a, _b;
        if (!e.isComposing) {
            if (e.target.value !== '') {
                (_a = sortableDataGridComponent === null || sortableDataGridComponent === void 0 ? void 0 : sortableDataGridComponent.current) === null || _a === void 0 ? void 0 : _a.updateDataGridRows(rows.filter(function (row) {
                    var rowStr = row.data.label.toLowerCase();
                    var evtStr = e.target.value.toLowerCase();
                    return rowStr.indexOf(evtStr) !== -1;
                }));
            }
            else {
                (_b = sortableDataGridComponent === null || sortableDataGridComponent === void 0 ? void 0 : sortableDataGridComponent.current) === null || _b === void 0 ? void 0 : _b.updateDataGridRows(rows);
            }
        }
    };
    var sortFieldProps = {
        kind: 'search',
        label: undefined,
        testId: 'coefficients-search-field',
        onChange: filterChanged,
    };
    return (React.createElement("div", null,
        React.createElement("div", { className: styles.ModelCoefficientsMetadata },
            React.createElement(prediction_model_results_viz_1.InfoCardComponent, { title: Localize_1.Messages.modelResultsCoefficientsNumberOfVariables(), value: modelFields.length - ignoreFields.length, width: MODEL_COEFFICIENTS_NUM_VAR_WIDTH }),
            React.createElement(prediction_model_results_viz_1.InfoCardComponent, { title: Localize_1.Messages.modelResultsCoefficientsInteractions(), value: rows.length, tooltip: Localize_1.Messages.modelResultsCoefficientsInteractionsDetails(), width: MODEL_COEFFICIENTS_INTERACTION_WIDTH }),
            React.createElement(prediction_model_results_viz_1.InfoCardComponent, { title: Localize_1.Messages.modelResultsCoefficientsIntercept(), value: React.createElement(React.Fragment, null, (0, NumberUtils_1.formatNumericField)({ value: interceptValue, fractionDigits: 2 })), tooltip: Localize_1.Messages.modelResultsCoefficientsInterceptDetails(), width: MODEL_COEFFICIENTS_INTERCEPT_WIDTH }),
            React.createElement(widgets_1.TextFieldWidget, __assign({}, sortFieldProps, { style: { marginRight: '12px' } })),
            React.createElement(widgets_1.ButtonWidget, { onClick: downloadCSV },
                React.createElement("div", { className: styles.ModelCoefficientsDownloadButton },
                    React.createElement(download_14px_small_1.DownloadSmall, null),
                    React.createElement("div", null, Localize_1.Messages.modelResultsCoefficientsDownload())))),
        React.createElement("div", { className: styles.DataGridContainer },
            React.createElement(SortableDataGrid_1.SortableDataGrid, __assign({ ref: sortableDataGridComponent }, sortedDataGridProps)))));
};
exports.default = ModelResultCoefficients;
//# sourceMappingURL=ModelResultCoefficients.js.map